import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, filter, map, of, switchMap, take, tap } from 'rxjs';
import { AppState } from '../app.state';
import { refreshPermissions } from '../store/permissions/permissions.actions';
import {
  selectCanAccessModule,
  selectCanDo,
  selectHasPermission,
  selectPermissionsLoaded,
} from '../store/permissions/permissions.selectors';

export interface PermissionRouteRequirement {
  module?: string;
  action?: string;
  permission?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const requirement = route.data['rbac'] as PermissionRouteRequirement | undefined;

    if (!requirement) {
      return of(true);
    }

    if (requirement.permission) {
      const permission = requirement.permission;
      return this.waitForPermissionsLoaded().pipe(
        switchMap(() =>
          this.store.select(selectHasPermission(permission)).pipe(take(1))
        ),
        map((hasAccess) => hasAccess || this.buildDeniedUrlTree(state.url))
      );
    }

    if (!requirement.module) {
      return of(true);
    }

    const selector = requirement.action
      ? selectCanDo(requirement.module, requirement.action)
      : selectCanAccessModule(requirement.module);

    return this.waitForPermissionsLoaded().pipe(
      switchMap(() => this.store.select(selector).pipe(take(1))),
      map((hasAccess) => hasAccess || this.buildDeniedUrlTree(state.url))
    );
  }

  private waitForPermissionsLoaded(): Observable<boolean> {
    return this.store.select(selectPermissionsLoaded).pipe(
      tap((loaded) => {
        if (!loaded) {
          this.store.dispatch(refreshPermissions());
        }
      }),
      filter((loaded) => loaded),
      take(1)
    );
  }

  private buildDeniedUrlTree(fromUrl: string): UrlTree {
    return this.router.createUrlTree(['/403'], {
      queryParams: { from: fromUrl },
    });
  }
}
